import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Modal, ModalBody, ModalFooter} from "reactstrap";
import {useTranslation} from "react-i18next";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {useStores} from "../../../stores/useStores";
import "./deleteSeriesModal.sass";
import ModalDetailHeader from "../../molecules/modalDetailHeader/modalDetailHeader";
import DateEdit from "../dateEdit/dateEdit";
import {runInAction} from "mobx";
import Flex from "../../atoms/common/flex";
import ButtonIcon from "../../atoms/button/buttonIcon";
import EventOccurrencesList from "../eventOccurrencesList/eventOccurrencesList";
import {QueryParams} from "../../../shared/enums/queryParams";
import {useHistory} from "react-router";
import {getEventsUrl} from "../../../utils/urlUtils";

export interface IDeleteSeriesModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirmBtnClicked: (effectiveFromDate: Date) => void;
}

const DeleteSeriesModal: React.FC<IDeleteSeriesModalProps> = ({
  isOpen,
  toggle,
  onConfirmBtnClicked
}) => {
  const history = useHistory();
  const {
    uiStore: {accountStore, eventsUiStore, modalStore},
    domainStore: {eventsDomainStore}
  } = useStores();
  const {t} = useTranslation(["translation", "event"]);
  const [effectiveFromDate] = useState(() => new DateObservable());
  const [occurrencesToManage, setOccurrencesToManage] = useState(
    eventsDomainStore.bookedEventOccurrenceInfos
  );
  const [hasBookedOccurrences, setHasBookedOccurrences] = useState(false);

  const navigateToEventOccurrenceManageBookings = (occurrenceDate: Date) => {
    const eventSeriesId = eventsDomainStore.editableEvent.getEventSeriesId();
    eventsDomainStore.setOccurrenceDate(occurrenceDate.toISOString());
    eventsUiStore.setEditingEventSeries(false);
    eventsUiStore.reloadEventDetailsAndBookings(eventSeriesId);
    eventsDomainStore.editableEvent.setIsDirty(false);

    const villageIdQueryParam = `${QueryParams.VillageId}=${eventsUiStore.selectableVillages?.selectedItem?.value}`;

    toggle();

    history.push(
      getEventsUrl(
        eventSeriesId!,
        villageIdQueryParam,
        eventsUiStore.selectedCalendarName,
        t("event:titles.manageBookings"),
        occurrenceDate,
        false
      )
    );
  };

  useEffect(() => {
    if (effectiveFromDate.date) {
      const bookedOccurrencesAfterDate =
        eventsDomainStore.bookedEventOccurrenceInfos.filter(
          (d) => d.occurrenceDate >= effectiveFromDate.date!
        );
      setOccurrencesToManage(bookedOccurrencesAfterDate);
      setHasBookedOccurrences(bookedOccurrencesAfterDate.length !== 0);
    }
  }, [eventsDomainStore.bookedEventOccurrenceInfos, effectiveFromDate.date]);

  const localNow = eventsDomainStore.getCurrentTime();

  // Get the event's end datetime
  const eventEndTime = new Date(
    eventsDomainStore.editableEvent.eventDateDetails.endDate.initialValue
  );

  let selectableOccurrenceDates = eventsDomainStore.eventOccurrenceInfos
    .filter((d) => {
      // eventOccurrenceDates are start datetimes
      // Get occurrence's end datetime
      eventEndTime.setFullYear(
        d.occurrenceDate.getFullYear(),
        d.occurrenceDate.getMonth(),
        d.occurrenceDate.getDate()
      );
      // Filter events that haven't ended
      return localNow < eventEndTime;
    })
    .map((d) => d.occurrenceDate);

  // Remove released occurrences if user cannot delete them
  if (!accountStore.abilities.can("Delete", "ReleasedEvent")) {
    selectableOccurrenceDates = selectableOccurrenceDates.filter(
      (occurrenceDate) =>
        occurrenceDate >= eventsDomainStore.getNextReleasePeriodStartDate()!
    );
  }

  if (selectableOccurrenceDates.length && !effectiveFromDate.date) {
    const firstOccurrenceDate = selectableOccurrenceDates[0];
    runInAction(() => {
      effectiveFromDate.date =
        eventsUiStore.selectedOccurrenceDate != undefined
          ? new Date(
              Math.max(
                eventsUiStore.selectedOccurrenceDate.getTime(),
                firstOccurrenceDate.getTime()
              )
            )
          : firstOccurrenceDate;
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="event-detail-modal"
      centered
    >
      <ModalDetailHeader isLoading={false} toggle={toggle}>
        <div className="header-container">
          <div className="header-column column-dynamic">
            {hasBookedOccurrences
              ? t("event:actions.unableToDeleteSeries")
              : t("event:actions.deleteThisSeries")}
          </div>
        </div>
      </ModalDetailHeader>
      <ModalBody>
        <>
          <div className="date-picker-container">
            <DateEdit
              enabledDates={selectableOccurrenceDates}
              value={effectiveFromDate}
              label={t("event:labels.effectiveFrom")}
              dateFormat={"dd/MM/yyyy"}
              placeholder={t("event:placeholders.effectiveFromDate")}
              overrideIsRequired={true}
              showTimeSelect={false}
              preventManualEntry={true}
              timeZone={eventsDomainStore.getTimeZone()}
            />
          </div>
          <div className="delete-series-message">
            {hasBookedOccurrences
              ? t("event:messages.confirmDeleteSeriesWhenBookingsForModal")
              : t("event:messages.confirmDeleteEventSeries")}
          </div>
        </>
      </ModalBody>
      {hasBookedOccurrences && (
        <ModalBody>
          <div className="manage-bookings-heading">
            {t("event:titles.manageTheseBookings")}
          </div>
          <div className="manage-bookings-scrollable">
            <EventOccurrencesList
              eventOccurrences={occurrencesToManage}
              onEditClick={navigateToEventOccurrenceManageBookings}
              timeZone={eventsDomainStore.getTimeZone()}
            />
          </div>
        </ModalBody>
      )}
      <ModalFooter
        tag={Flex}
        justify={modalStore.footerButtonAlignment}
        className="delete-series-modal-footer"
      >
        {!hasBookedOccurrences && (
          <ButtonIcon
            type={"primary-negative"}
            onClick={() => onConfirmBtnClicked(effectiveFromDate.date!)}
            isDisabled={!effectiveFromDate.date}
          >
            {t("event:actions.confirmDelete")}
          </ButtonIcon>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default observer(DeleteSeriesModal);
